import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '8 October 2026';
const COMPANY = 'FactreeTech';
const CONTACT_EMAIL = 'privacy@factree.com.au';
const WEB_URL = 'https://tech.factree.com.au';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3">
      <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
      <div className="space-y-3 text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-sm bg-white/90 border-b border-neutral-200">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <span className="text-xl font-bold text-neutral-900">{COMPANY}</span>
            </Link>
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight text-neutral-900">Privacy Policy</h1>
          <p className="text-sm text-neutral-500">Effective date: {EFFECTIVE_DATE}</p>
          <p className="text-neutral-700 leading-relaxed">
            This Privacy Policy explains how {COMPANY} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses,
            discloses and protects information when you use the {COMPANY} web application, mobile apps
            (Android and iOS), connected hardware and related services (together, the &ldquo;Service&rdquo;).
            {COMPANY} is a farm automation platform used by farm businesses, orchardists, agronomists and
            agricultural contractors. If you use the Service as an employee, contractor, or member of a
            subscribing organisation (&ldquo;Customer&rdquo;), the Customer is the controller of your data and
            this policy applies in addition to the Customer&rsquo;s own privacy arrangements.
          </p>
        </div>

        {/* Contents */}
        <nav aria-label="Contents" className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <p className="text-sm font-semibold text-neutral-900 mb-2">Contents</p>
          <ol className="list-decimal list-inside text-sm text-primary-700 space-y-1">
            <li><a href="#who-we-are" className="hover:underline">Who we are</a></li>
            <li><a href="#info-we-collect" className="hover:underline">Information we collect</a></li>
            <li><a href="#location" className="hover:underline">Location data and background GPS tracking</a></li>
            <li><a href="#sensor-data" className="hover:underline">Sensor, irrigation and equipment data</a></li>
            <li><a href="#camera" className="hover:underline">Camera, photos and files</a></li>
            <li><a href="#notifications" className="hover:underline">Push notifications</a></li>
            <li><a href="#cookies" className="hover:underline">Cookies and similar technologies</a></li>
            <li><a href="#how-we-use" className="hover:underline">How we use information</a></li>
            <li><a href="#legal-bases" className="hover:underline">Legal bases for processing</a></li>
            <li><a href="#sharing" className="hover:underline">How we share information</a></li>
            <li><a href="#third-parties" className="hover:underline">Third-party services</a></li>
            <li><a href="#retention" className="hover:underline">Data retention</a></li>
            <li><a href="#security" className="hover:underline">Security</a></li>
            <li><a href="#international" className="hover:underline">International transfers</a></li>
            <li><a href="#your-rights" className="hover:underline">Your rights and choices</a></li>
            <li><a href="#children" className="hover:underline">Children</a></li>
            <li><a href="#changes" className="hover:underline">Changes to this policy</a></li>
            <li><a href="#contact" className="hover:underline">Contact us</a></li>
          </ol>
        </nav>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            {COMPANY} provides a multi-tenant software-as-a-service platform for farm, orchard and nursery
            operations, including staff time tracking, leave management, spray recording and chemical
            compliance, farm job scheduling, nursery inventory, cultivar registry, Plant Breeder&rsquo;s
            Rights workflows, weather stations, sensor telemetry, irrigation control and equipment
            management. Our service is available at <a href={WEB_URL} className="text-primary-600 hover:underline">{WEB_URL}</a>{' '}
            and via the {COMPANY} mobile apps on Google Play and the Apple App Store.
          </p>
          <p>
            When you use {COMPANY} as part of an organisation, that organisation determines what data is
            collected and how long it is kept. {COMPANY} processes that data on the organisation&rsquo;s
            behalf. When you use {COMPANY} directly (for example to create an account or verify a
            certificate), {COMPANY} is the controller.
          </p>
        </Section>

        <Section id="info-we-collect" title="2. Information we collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Account and profile data:</strong> name, email address, phone number, password (stored
              hashed), employee number, role, organisation membership, profile photo, and
              multi-factor-authentication registrations (TOTP secrets, WebAuthn/passkey credentials).
            </li>
            <li>
              <strong>Authentication data:</strong> JWT access tokens held on your device, HTTP-only refresh
              token cookies, email verification tokens, password reset tokens, MFA challenge tokens, and
              session activity logs.
            </li>
            <li>
              <strong>Work records:</strong> timesheet entries (clock-in and clock-out times, breaks, work
              location, associated job or spray), leave requests, scheduled hours, public holidays, pay
              cycles, and time-in-lieu balances; hours worked on each job and the block or cultivar area
              they were worked in; and harvest bins attributed to the pickers who filled them.
            </li>
            <li>
              <strong>Operational records:</strong> spray applications (chemicals, rates, coverage,
              weather, operator), chemical inventory, purchase orders and the photos of receipts attached to
              them, farm jobs (tasks, assignees,
              location tracks, coverage), nursery batches, growing plans, orders (and, where an order
              was imported from a customer&rsquo;s purchase order file, that file as it was uploaded), cultivar records,
              licences, declarations, certificates, equipment pre-start checks, maintenance, service and
              parts records.
            </li>
            <li>
              <strong>Inductions:</strong> which machines a person has been signed off to operate, by whom and
              when, and a record of each company policy or safety induction they acknowledged — the version they
              read, the date and time, the name they signed with, and the IP address and device (browser) they
              used, so the acknowledgement can be shown later.
            </li>
            <li>
              <strong>Time in lieu agreements:</strong> when an employee asks to bank overtime as time in lieu
              instead of being paid for it, we keep the agreement they accepted (its exact text and version), the
              date and time, and the IP address and device (browser) they used, and who approved, declined or
              ended it for their employer and when.
            </li>
            <li>
              <strong>Compliance and audit:</strong> when your organisation gives an auditor or certifier
              read-only access, we record the auditor&rsquo;s name, email address and organisation, the
              one-time sign-in codes sent to them (stored only in scrambled form), and a log of each page and
              download they open with its time, IP address and browser, which your organisation can see. We
              also keep the findings they record and your organisation&rsquo;s responses, and a frozen copy of
              each compliance pack your organisation generates.
            </li>
            <li>
              <strong>Workers without a login:</strong> an organisation can record people who do not have
              their own account, such as seasonal pickers and contractor crews — their name, a picker code,
              the labour provider they came through, and their hours and bins. The organisation enters and
              controls this information.
            </li>
            <li>
              <strong>Spatial data:</strong> farm, block and cultivar-area boundaries drawn as GeoJSON
              polygons; positional traces captured during spray, job and equipment sessions.
            </li>
            <li>
              <strong>Sensor and hardware data:</strong> readings from soil-moisture probes, tank-level
              sensors, weather stations, ESP-NOW irrigation controllers and any other devices you connect,
              including device identifiers, firmware versions, signal strength and configuration.
            </li>
            <li>
              <strong>Billing and subscription data:</strong> organisation billing email, subscription
              selections, seat counts, licence tiers, invoice history and Xero contact identifiers. We do
              not store your full credit-card number; card payments (where available) are processed by the
              payment provider and only a reference is retained.
            </li>
            <li>
              <strong>Device and usage data:</strong> IP address, browser type and version, operating
              system, device model, app version, language, time zone, pages and features used, error logs
              and performance telemetry.
            </li>
            <li>
              <strong>Communications:</strong> emails you send us (including verification and password reset
              flows sent via Microsoft Graph), support requests, and notifications you receive.
            </li>
            <li>
              <strong>Files you upload:</strong> photos, documents, certificates, chemical labels, safety
              data sheets and other attachments you add to records.
            </li>
          </ul>
        </Section>

        <Section id="location" title="3. Location data and background GPS tracking">
          <p>
            {COMPANY} uses precise location data to power core operational features. This is the most
            sensitive category of data we collect, so we describe it in detail.
          </p>
          <p className="font-semibold">When we collect location data:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Clock in and clock out:</strong> a single location fix is captured and stored against
              your timesheet entry so your employer can verify on-site work. This happens only at the
              moment you press the clock button.
            </li>
            <li>
              <strong>Active spray applications:</strong> while a spray is in the &ldquo;in progress&rdquo;
              state, the app records a continuous GPS trace. The trace is used to calculate the area
              covered, detect missed rows, and produce a compliance record for chemical use.
            </li>
            <li>
              <strong>Active farm jobs:</strong> while a farm job is in progress, the app records a
              continuous GPS trace to verify coverage and attribute the work to the operator.
            </li>
            <li>
              <strong>Equipment sessions:</strong> when you start a session on a tracked vehicle or
              implement, the app records position so engine hours, service intervals and working areas can
              be derived.
            </li>
            <li>
              <strong>Starting a harvest bin:</strong> a single location fix is captured when you start a bin,
              so the bin can be placed in the right block and row.
            </li>
            <li>
              <strong>Hours on a job:</strong> while your time is open on a job whose type of work asks for it,
              the app records one position about every 10 minutes while it is open, so your hours can be
              placed in the block you are working in. It stops when your time on the job stops.
            </li>
          </ul>
          <p className="font-semibold">Background location:</p>
          <p>
            On Android and iOS the app requests background-location permission so that GPS capture
            continues when the screen is off, the app is in the background, or the device is locked. This
            is required because spray runs and farm jobs often last hours, the app cannot be held in the
            foreground during active work, and a break in the track would corrupt the compliance record.
            When background tracking is active:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>A persistent notification is displayed so you always know tracking is on.</li>
            <li>Tracking stops automatically when you finish the spray or job, end the equipment session, or clock out.</li>
            <li>You can stop tracking at any time by completing the active operation or revoking location permission in your device settings.</li>
            <li>We do not track your location when you are not clocked in and no active spray, job or equipment session is running.</li>
          </ul>
          <p className="font-semibold">What we do with your location data:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Record which blocks and rows were sprayed or worked, and at what time.</li>
            <li>Calculate area coverage, application rates, and compliance with label requirements.</li>
            <li>Attribute work to an operator for payroll and productivity reporting.</li>
            <li>Verify on-site attendance for timesheet entries.</li>
            <li>Match positions to farm, block and cultivar-area polygons the organisation has defined.</li>
          </ul>
          <p className="font-semibold">What we do not do:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>We do not sell, rent or share your location data with advertisers.</li>
            <li>We do not use your location for marketing, profiling, or any purpose unrelated to the active operation you are performing.</li>
            <li>We do not track your location outside of an active clock-in, spray, farm job or equipment session.</li>
            <li>We do not use location to identify your home address, personal travel patterns or any third party.</li>
          </ul>
          <p>
            Location permission can be granted, narrowed (for example to &ldquo;while using&rdquo;) or
            revoked at any time through your device&rsquo;s system settings. Revoking permission will
            disable the features above and your employer may not be able to accept related timesheet,
            spray or job submissions.
          </p>
        </Section>

        <Section id="sensor-data" title="4. Sensor, irrigation and equipment data">
          <p>
            If your organisation uses {COMPANY}&rsquo;s hardware features, we also collect data reported by
            connected devices, including soil-moisture sensors, tank-level sensors, ESP-NOW irrigation
            controllers, weather stations and pre-start / telematics data from tractors, sprayers and other
            equipment. This data is used to run irrigation schedules, trigger alerts, plan maintenance and
            produce compliance reports. Device data is associated with the organisation that owns the
            device, not with individual users, except where a user is the operator of a session.
          </p>
        </Section>

        <Section id="camera" title="5. Camera, photos and files">
          <p>
            The app may request camera, microphone and photo-library permissions so you can attach photos
            to records (for example a pre-start inspection, a nursery row or a chemical label). Media you
            capture through the app is uploaded to our servers only when you add it to a record. We do not
            scan your camera roll and we do not upload media in the background.
          </p>
        </Section>

        <Section id="notifications" title="6. Push notifications">
          <p>
            If you allow notifications, we use Firebase Cloud Messaging (on Android) and Apple Push
            Notification service (on iOS) to deliver operational alerts such as timesheet reminders,
            approval requests, job assignments, irrigation alarms and credential expiry warnings. Device
            push tokens are stored against your account and rotated when the operating system reissues
            them. You can disable notifications at any time in your device settings.
          </p>
        </Section>

        <Section id="cookies" title="7. Cookies and similar technologies">
          <p>We use a small number of strictly necessary cookies and local-storage entries:</p>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Refresh-token cookie:</strong> HTTP-only, secure, SameSite, used to keep you signed in.</li>
            <li><strong>Access-token storage:</strong> short-lived access token held in the browser&rsquo;s local storage or the app&rsquo;s secure store.</li>
            <li><strong>Preferences:</strong> active organisation, theme and similar UI settings.</li>
          </ul>
          <p>
            We do not use third-party advertising cookies or cross-site tracking on the {COMPANY} platform.
          </p>
        </Section>

        <Section id="how-we-use" title="8. How we use information">
          <p>We use the information above to:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Provide, operate, maintain and improve the Service.</li>
            <li>Authenticate you and protect your account (including MFA and suspicious-activity detection).</li>
            <li>Record work performed, generate payroll exports, and produce statutory reports (for example chemical use records and PBR declarations).</li>
            <li>Deliver transactional email, push notifications and in-app alerts.</li>
            <li>Respond to support requests and communicate changes to the Service.</li>
            <li>Invoice subscribing organisations and record payments.</li>
            <li>Detect, investigate and prevent fraud, abuse and security incidents.</li>
            <li>Comply with our legal obligations.</li>
          </ul>
          <p>
            We do not use your personal data to train third-party general-purpose machine-learning models,
            and we do not sell personal data.
          </p>
        </Section>

        <Section id="legal-bases" title="9. Legal bases for processing">
          <p>
            Where data-protection law (such as the Australian Privacy Principles, the GDPR or the UK GDPR)
            requires a legal basis, we rely on: performance of a contract with you or your organisation;
            our or our customer&rsquo;s legitimate interests in running a farm business (including
            compliance, safety, productivity and fraud prevention); your consent where we ask for it (for
            example to enable background location or push notifications); and compliance with legal
            obligations.
          </p>
        </Section>

        <Section id="sharing" title="10. How we share information">
          <p>We share information only as follows:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Within your organisation:</strong> with administrators, HR staff, supervisors, agronomists and other authorised users of your organisation&rsquo;s tenant, according to their assigned roles and module licences.
            </li>
            <li>
              <strong>With cross-organisation advisors:</strong> if your organisation grants an external agronomist or advisor access to the spray module, their access is scoped to that module and recorded.
            </li>
            <li>
              <strong>With auditors and certifiers your organisation chooses:</strong> your organisation can
              give a named auditor (for example for GlobalG.A.P. or Freshcare certification) read-only access to
              selected records for a period it sets, or hand them a compliance pack. This can include your name,
              role, spray records you made, your chemical and other credentials (type, number, issuer and
              dates), and the inductions and policies you have been signed off on or acknowledged. It never
              includes your contact details, pay, date of birth, health or injury details, or who made an
              anonymous safety report. Access is time-limited, can be ended at any time, and every page the
              auditor opens is logged. For a reviewer who is not certifying the organisation, it can replace
              names with &ldquo;Person 1&rdquo;, &ldquo;Person 2&rdquo; and mask credential numbers.
            </li>
            <li>
              <strong>With service providers:</strong> hosting, email delivery, error tracking, push notification and billing providers acting on our instructions (see section 11).
            </li>
            <li>
              <strong>With public registers, where required:</strong> for example Plant Breeder&rsquo;s Rights applications and cultivar declarations.
            </li>
            <li>
              <strong>In legal situations:</strong> when we are required by law, regulation, court order, or to protect the rights, property or safety of {COMPANY}, our customers or others.
            </li>
            <li>
              <strong>In a business transfer:</strong> in connection with a merger, acquisition or sale of assets, subject to the receiving party honouring this policy.
            </li>
          </ul>
        </Section>

        <Section id="third-parties" title="11. Third-party services">
          <p>We use the following providers to run the Service:</p>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Google Cloud Platform (Cloud Run, Artifact Registry, Cloud Storage):</strong> hosting and file storage, primarily in the <code>australia-southeast1</code> region.</li>
            <li><strong>PostgreSQL database provider:</strong> primary data store.</li>
            <li><strong>Microsoft Graph / Microsoft 365:</strong> outbound transactional email (verification, password reset, notifications), including despatch notices a nursery chooses to send its own customers through the Service &mdash; the customer&rsquo;s name, email address, delivery address and the trees on the order, sent on the nursery&rsquo;s behalf &mdash; and supplier manifests a nursery chooses to email to the addresses it names (purchase order and docket numbers, stores, quantities, and the contact name, phone and email it enters).</li>
            <li><strong>Firebase Cloud Messaging and Apple Push Notification service:</strong> push-notification delivery.</li>
            <li><strong>Open-Meteo:</strong> weather forecast and actual-conditions data for spray planning and recording. Open-Meteo receives the coordinates of the relevant farm or spray but no personal identifiers.</li>
            <li><strong>Xero Payroll and MYOB:</strong> only where your organisation connects them. Employee names and identifiers, approved timesheets, leave applications and pay-item mappings are sent to the connected system, and leave balances are read back from it. Where your organisation maps time in lieu to a Xero leave type, the hours each employee banks are also added to their draft payslip in Xero.</li>
            <li><strong>Xero (your organisation&rsquo;s own accounting file):</strong> only where your organisation connects it for nursery sales. Your customers&rsquo; business names, ABNs and email addresses, and the invoices and credit notes you raise to them (including the store, purchase order number and what was sold), are sent to your Xero organisation, and the payments recorded there are read back. Where your organisation also switches it on for purchase orders, the orders you send to suppliers (the supplier&rsquo;s name, email address and ABN, the order number, the farm it is for, the items, quantities and costs) are sent to your Xero organisation too, and whether each has been billed is read back.</li>
            <li><strong>Stripe:</strong> card payments for nursery website orders. Card details are entered directly with Stripe and never reach our servers; we receive the payment outcome, amount and the buyer&rsquo;s name and email. Where an order paid for by card is despatched short and the remainder is not backordered, the price of the trees not supplied is refunded to the same card through Stripe. A nursery can also connect its own Stripe account to take cards at its counter: those payments are made on Stripe&rsquo;s page into the nursery&rsquo;s account, and we send Stripe the order number and amount, and keep the payment&rsquo;s reference and whether it was paid. Cash taken at a counter is recorded in the nursery&rsquo;s till records with who took it.</li>
            <li><strong>Adobe Acrobat Sign:</strong> electronic signing of cultivar licence and order agreements. Signers&rsquo; names, email addresses and the agreement documents are sent to Adobe for signature.</li>
            <li><strong>Anthropic (Claude):</strong> AI-assisted research features, such as looking up a pest or disease or a variety&rsquo;s plant-protection status. The names and search terms you enter, and relevant crop lists, are sent to Anthropic, which may use web search to answer. We do not send personal information about you or your workers for these features.</li>
            <li><strong>Google Maps Platform (Places):</strong> address suggestions while you type an address. The text you type is sent to Google through our servers; Google&rsquo;s results are not stored beyond the address you choose.</li>
            <li><strong>Xero:</strong> subscription invoicing. When your organisation purchases a subscription, we create a Xero contact using your organisation&rsquo;s billing name and email and issue an invoice there.</li>
            <li><strong>IP Australia, UPOV PLUTO, USPTO, EU CPVO, NZ PVR:</strong> public Plant Breeder&rsquo;s Rights and plant-patent databases queried on demand for cultivar lookups.</li>
            <li><strong>Vicmap, land.vic.gov.au, planning.vic.gov.au:</strong> public Victorian land-registry and planning data queried to verify farm polygons.</li>
            <li><strong>Map tile providers:</strong> for displaying interactive maps.</li>
          </ul>
          <p>
            Each provider only receives the minimum data necessary to perform its function, and each is
            bound by its own terms and privacy practices.
          </p>
        </Section>

        <Section id="retention" title="12. Data retention">
          <p>
            We retain personal data for as long as your account and your organisation&rsquo;s tenant are
            active, plus the period required to meet legal, accounting, tax and compliance obligations
            (commonly seven years for financial and chemical-use records in Australia). Location traces
            attached to sprays, jobs and equipment sessions are retained with those records for the same
            period because they are part of the compliance record. When a record is no longer required, it
            is deleted or de-identified.
          </p>
          <p>
            If you delete your account or your organisation terminates its subscription, we delete or
            anonymise personal data within a reasonable period, except where we are required to retain it.
          </p>
        </Section>

        <Section id="security" title="13. Security">
          <p>
            We use industry-standard safeguards including HTTPS/TLS for data in transit, encrypted storage
            for sensitive secrets (including AES-256 encryption of our Xero OAuth tokens), hashed
            passwords, HTTP-only secure cookies for refresh tokens, optional multi-factor authentication
            (TOTP, WebAuthn / passkeys and one-time codes sent to your email address), role-based access control, per-organisation tenant isolation
            at the database layer, parameterised SQL queries, rate limiting and audit logging. No system
            can be made completely secure; please notify us promptly if you suspect your account has been
            compromised.
          </p>
        </Section>

        <Section id="international" title="14. International transfers">
          <p>
            {COMPANY} hosts production data in Australia (<code>australia-southeast1</code>). Some of our
            service providers (for example Microsoft Graph, Firebase, Apple Push Notification service and
            Xero) may process data in other regions. Where data is transferred outside your country we
            rely on the provider&rsquo;s standard contractual clauses, adequacy decisions or equivalent
            safeguards.
          </p>
        </Section>

        <Section id="your-rights" title="15. Your rights and choices">
          <p>Depending on where you live, you may have the right to:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Access a copy of the personal information we hold about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of personal information, subject to our legal retention obligations.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Withdraw consent you have given (for example for background location or push notifications) at any time.</li>
            <li>Lodge a complaint with your local data-protection authority (in Australia, the Office of the Australian Information Commissioner).</li>
          </ul>
          <p>
            If your data is held on behalf of an organisation, we will usually refer your request to that
            organisation. Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a> to exercise any of these rights.
          </p>
        </Section>

        <Section id="children" title="16. Children">
          <p>
            {COMPANY} is not directed to children under the age of 16 and we do not knowingly collect
            personal information from children. If you believe a child has provided us with personal
            information, please contact us and we will delete it.
          </p>
        </Section>

        <Section id="changes" title="17. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be notified through
            the app or by email to account administrators. The &ldquo;Effective date&rdquo; at the top of
            this page shows when the current version was published.
          </p>
        </Section>

        <Section id="contact" title="18. Contact us">
          <p>
            Questions, requests or complaints about this policy or our handling of your personal
            information can be sent to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-400 mt-16">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8 text-center space-y-2">
          <p className="text-sm">&copy; {new Date().getFullYear()} {COMPANY}</p>
          <p className="text-xs text-neutral-500">
            <Link to="/" className="hover:text-neutral-300">Home</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/terms" className="hover:text-neutral-300">Terms</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/privacy" className="hover:text-neutral-300">Privacy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
